<template>
  <!-- One bar above the charges: selected rows are excluded with one reason, or the selection is cleared. Rows are
       approved in the customer's review. -->
  <div class="financial__bulk" role="group" aria-labelledby="financial-bulk-count">
    <strong id="financial-bulk-count" tabindex="-1">{{ numberLabel(count) }} selected</strong>
    <button v-if="selectAll" class="financial__link" type="button" :disabled="busy" @click="chooseAll">
      Select all {{ numberLabel(selectAll) }} rows in this list
    </button>
    <form novalidate @submit.prevent="exclude">
      <label for="financial-bulk-note">Reason for excluding</label>
      <div class="financial__bulk-note">
        <input
          id="financial-bulk-note"
          v-model="note"
          class="form-control"
          autocomplete="off"
          :disabled="busy"
          :aria-invalid="!!error"
          :aria-describedby="error ? 'financial-bulk-note-error' : undefined"
        />
        <p v-if="error" id="financial-bulk-note-error" class="financial__error">{{ error }}</p>
      </div>
      <button class="financial__button financial__button--danger-outline" type="submit" :disabled="busy">
        Exclude {{ numberLabel(count) }}
      </button>
    </form>
    <button class="financial__button financial__button--quiet" type="button" :disabled="busy" @click="clear">Clear selection</button>
  </div>
</template>
<script lang="ts">
import { defineComponent, nextTick, ref, type Ref } from 'vue'

export default defineComponent({
  name: 'FinancialBulkReview',
  props: {
    count: { type: Number, required: true },
    // How many rows the filtered list holds when one click can select them all; 0 hides that link.
    selectAll: { type: Number, default: 0 },
    busy: { type: Boolean, default: false },
  },
  emits: ['exclude', 'select-all', 'clear'],
  setup(_, { emit }) {
    const note: Ref<string> = ref('')
    const error: Ref<string> = ref('')
    const numberLabel = (value: number): string => value.toLocaleString('en-GB')
    // A missing reason is shown under the input, and focus moves there once the input describes it.
    const exclude = async (): Promise<void> => {
      error.value = note.value.trim() ? '' : 'Add the reason for excluding these rows.'
      if (!error.value) {
        emit('exclude', { note: note.value.trim() })
        return
      }
      await nextTick()
      document.getElementById('financial-bulk-note')?.focus()
    }
    // The link goes once every row is selected; focus stays in the bar, on the new count.
    const chooseAll = async (): Promise<void> => {
      emit('select-all')
      await nextTick()
      document.getElementById('financial-bulk-count')?.focus()
    }
    const clear = (): void => {
      emit('clear')
    }
    return { note, error, numberLabel, exclude, chooseAll, clear }
  },
})
</script>
